import {
  connectBaseApi,
  createRpcQuery,
  createRpcMutation,
  organizationClient,
  adminClient,
  ConnectProto,
} from "@racoon/api";
import { typeTag } from "../locations/locations.grpc";

export const orgGrpcApi = connectBaseApi.injectEndpoints({
  endpoints: (build) => ({
    // Replaces the legacy REST GET /v1/platform/organization/list/related.
    // Returns ConnectProto.OrganizationList; consumers read `.organizations`.
    // Omit `status` to return all counterparties; pass an explicit ContractStatus to filter.
    listCounterparties: createRpcQuery(build, organizationClient, "listCounterparties", {
      providesTags: [{ type: "Company", id: "LIST" }],
    }),
    listServiceReceivers: createRpcQuery(build, organizationClient, "listServiceReceivers", {
      providesTags: [{ type: "Company", id: "LIST" }],
    }),
    childrenOrganizationList: createRpcQuery(
      build,
      organizationClient,
      "listChildrenOrganization",
      {
        providesTags: [{ type: "Company", id: "LIST" }],
      },
    ),
    // The organization and every descendant at any depth, root first; parent_id on each
    // builds the tree. Picks the node of an access group, or the divisions a responsible zone can hold.
    listOrganizationSubtree: createRpcQuery(build, organizationClient, "listOrganizationSubtree", {
      providesTags: [{ type: "Company", id: "LIST" }],
    }),
    // The user's own organization and every descendant provider: whom they can hand work to.
    listAccessibleOrganizations: createRpcQuery(
      build,
      organizationClient,
      "listAccessibleOrganizations",
      { providesTags: [{ type: "Company", id: "LIST" }] },
    ),
    listOrganizations: createRpcQuery(build, adminClient, "listOrganizations", {
      providesTags: ["Company"],
    }),
    getOrganization: createRpcQuery(build, organizationClient, "getOrganization", {
      providesTags: (result, error, arg) => [{ type: "Company", id: String(arg.organizationId) }],
    }),
    createOrganization: createRpcMutation(build, adminClient, "createOrganization", {
      invalidatesTags: [{ type: "Company", id: "LIST" }],
    }),
    createServiceReceiver: createRpcMutation(build, organizationClient, "createServiceReceiver", {
      invalidatesTags: [{ type: "Company", id: "LIST" }],
    }),
    updateOrganization: createRpcMutation(build, organizationClient, "updateOrganization", {
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Company", id: String(id) },
        { type: "Company", id: "LIST" },
        "Company",
        "Employee",
      ],
    }),
    // getOrganizationSettings resolves the org from the auth context (current user's
    // own org); it takes an empty request, so the hook is called with `{}`. Every org
    // is guaranteed to have a settings row (auto-provisioned at org creation, and
    // self-healed by the backend on read for any legacy org), so there is no
    // client-facing create path.
    getOrganizationSettings: createRpcQuery(build, organizationClient, "getSetting", {
      providesTags: ["OrgSettings"],
    }),
    // The disposal site selection changes which sites ListDestinationLocations returns.
    // Another org's settings (GetOrganization) are read afresh by the dialog that edits them.
    updateOrganizationSettings: createRpcMutation(build, organizationClient, "updateSetting", {
      invalidatesTags: (_r, _e, { disposalSiteOrgIds }) =>
        disposalSiteOrgIds
          ? ["OrgSettings", typeTag(ConnectProto.LocationType.DISPOSAL_SITE)]
          : ["OrgSettings"],
    }),
    // Replaces the caller's org list of disabled job types; read back via getOrganizationSettings.
    updateDisabledJobTypes: createRpcMutation(build, organizationClient, "updateDisabledJobTypes", {
      invalidatesTags: ["OrgSettings"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListCounterpartiesQuery,
  useListServiceReceiversQuery,
  useChildrenOrganizationListQuery,
  useListOrganizationSubtreeQuery,
  useListAccessibleOrganizationsQuery,
  useListOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useCreateServiceReceiverMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation,
  useUpdateDisabledJobTypesMutation,
} = orgGrpcApi;
export const useUpdateServiceReceiverMutation = orgGrpcApi.useUpdateOrganizationMutation;
export const useGetOrganizationByIdQuery = orgGrpcApi.useGetOrganizationQuery;
